/**
 * X ADS › NEW CAMPAIGN — /x-ads/campaigns/new
 *
 *   1 Campaign    name, funding instrument, budgets            → POST campaigns (paused)
 *   2 Line item   objective, product, placements, bidding, goal, schedule   (form only)
 *   3 Targeting   …                                            → POST line-items with targeting (paused)
 *                                                                (later edits: PUT line-items/{id}/targeting)
 *   4 Creative    pick posts or create one                     → POST line-items/{id}/promoted-posts
 *   5 Review
 *
 * Whatever already exists on X is listed on the right and never created twice: a failed step
 * is retried on its own.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, CircleDashed, ExternalLink, Plus, RefreshCw, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { DateTimePicker } from '@/components/DateTimePicker';
import { PostComposer } from '@/components/x-ads/PostComposer';
import { TargetingForm } from '@/components/x-ads/TargetingForm';
import { MultiPicker } from '@/components/x-ads/MultiPicker';
import { XCheck } from '@/components/x-ads/XCheck';
import { XSelect } from '@/components/x-ads/XSelect';
import { XAdsNotice, XAdsShell } from '@/components/x-ads/XAdsShell';
import {
  biddingProblem, creativeNeed, emptyTargeting, fitToObjective, goalLabel, goalsFor, isOffNetwork, needsApp, needsCategories, placementLabel,
  placementsFor, productLabel, productsFor, strategyAllowed, targetingCount, toTargetingInput, togglePlacement, usesMediaCreatives, type Opt, type TargetingState,
} from '@/lib/x-ads/lineItemRules';
import { rememberPost, withRecentPosts } from '@/lib/x-ads/recentPosts';
import { useXConnection } from '@/lib/x-ads/connection';
import { dateTime, fundingName, label, localDayStart, localInputToIso, microsMoney, postText, toMicros } from '@/lib/x-ads/format';
import { XA_HOME, XA_NEW } from '@/lib/x-ads/routes';
import { errMsg, xAdsService, type XCampaign, type XConstants, type XFundingInstrument, type XLineItem, type XMedia, type XMediaCreative, type XPost, type XPromotedPost } from '@/services/x-ads.service';

const STEPS = ['Campaign', 'Line item', 'Targeting', 'Creative', 'Review'] as const;

interface LineItemForm {
  name: string; objective: string; productType: string; placements: string[]; bidStrategy: string; bid: string;
  goal: string; advertiserDomain: string; start: string; end: string;
  /** IAB categories of the brand, and the app an app objective promotes */
  categories: Opt[]; appStore: 'ios' | 'android'; appId: string;
}

export default function XAdsCampaignWizard() {
  const navigate = useNavigate();
  const { uid, account } = useXConnection();
  const currency = account?.currency ?? '';
  const [step, setStep] = useState(0);
  const [constants, setConstants] = useState<XConstants | null>(null);
  const [funding, setFunding] = useState<XFundingInstrument[] | null>(null);

  // Step 1
  const [campaignName, setCampaignName] = useState('');
  const [fundingId, setFundingId] = useState('');
  const [daily, setDaily] = useState('');
  const [total, setTotal] = useState('');
  // Step 2
  const [li, setLi] = useState<LineItemForm>({
    name: '', objective: 'ENGAGEMENTS', productType: 'PROMOTED_TWEETS', placements: ['ALL_ON_TWITTER'], bidStrategy: 'AUTO', bid: '',
    goal: '', advertiserDomain: '', start: '', end: '', categories: [], appStore: 'ios', appId: '',
  });
  // Step 3
  const [targeting, setTargeting] = useState<TargetingState>(emptyTargeting);
  // Step 4
  const [posts, setPosts] = useState<XPost[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [mode, setMode] = useState<'pick' | 'create'>('pick');

  // What exists on X.
  const [campaign, setCampaign] = useState<XCampaign | null>(null);
  const [lineItem, setLineItem] = useState<XLineItem | null>(null);
  const [savedTargeting, setSavedTargeting] = useState<string | null>(null);
  const [targetingError, setTargetingError] = useState<string | null>(null);
  const [promoted, setPromoted] = useState<XPromotedPost[]>([]);
  // Pre-roll / MEDIA line items run library videos (media creatives) instead of posts.
  const [videos, setVideos] = useState<XMedia[] | null>(null);
  const [pickedVideos, setPickedVideos] = useState<string[]>([]);
  const [landingUrl, setLandingUrl] = useState('https://');
  const [mediaCreatives, setMediaCreatives] = useState<XMediaCreative[]>([]);
  const [busy, setBusy] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  useEffect(() => { xAdsService.constants().then(setConstants).catch(() => undefined); }, []);
  useEffect(() => {
    if (!uid) return;
    setFundingId(account?.fundingInstrumentId ?? '');
    xAdsService.fundingInstruments(uid).then((list) => {
      setFunding(list);
      // Keep the account's instrument when it is listed; otherwise the first one that can pay.
      setFundingId((cur) => (list.some((f) => f.id === cur) ? cur : list.find((f) => f.ableToFund)?.id ?? list[0]?.id ?? ''));
    }).catch(() => setFunding([]));
  }, [uid, account?.fundingInstrumentId]);
  // IAB categories (brand description), loaded the first time a line item needs them.
  const [categories, setCategories] = useState<Opt[] | null>(null);
  const wantsCategories = needsCategories(li.objective, li.productType, li.placements);
  useEffect(() => {
    if (!wantsCategories || categories || !uid) return;
    xAdsService.lookup(uid, 'categories').then((r) => setCategories(r.filter((c) => c.targetingValue).map((c) => ({
      value: c.targetingValue!, label: c.name || c.targetingValue!, hint: c.parentName || c.targetingValue,
    })))).catch(() => setCategories([]));
  }, [wantsCategories, categories, uid]);
  const videoCreative = usesMediaCreatives(li.objective, li.productType);
  useEffect(() => {
    if (step !== 3 || !videoCreative || videos || !uid) return;
    xAdsService.media(uid, 'VIDEO').then(setVideos).catch(() => setVideos([]));
  }, [step, videoCreative, videos, uid]);
  useEffect(() => {
    if (step !== 3 || posts || !uid) return;
    xAdsService.posts(uid, 'PUBLISHED', 'NULLCAST').then((list) => setPosts(withRecentPosts(uid, list))).catch(() => setPosts(withRecentPosts(uid, [])));
  }, [step, posts, uid]);

  const targetingBody = useMemo(() => toTargetingInput(targeting), [targeting]);
  const targetingSig = JSON.stringify(targetingBody);
  const bidMicros = toMicros(li.bid);
  const setLiField = <K extends keyof LineItemForm>(k: K, v: LineItemForm[K]) => setLi((s) => ({ ...s, [k]: v }));

  // ── Validation ──────────────────────────────────────────────────────────
  const campaignProblem = (): string | null => {
    if (!campaignName.trim()) return 'Give the campaign a name.';
    const d = toMicros(daily);
    if (!d || d < 1) return 'Enter a daily budget.';
    const t = toMicros(total);
    if (total && (!t || t < d)) return 'The total budget must be at least the daily budget.';
    if (funding && funding.length === 0 && !fundingId) return 'This ads account has no funding instrument. Add a payment method on ads.x.com.';
    return null;
  };
  const lineItemProblem = (): string | null => {
    if (!li.name.trim()) return 'Give the line item a name.';
    if (!li.objective) return 'Choose an objective.';
    if (li.placements.length === 0) return 'Choose at least one placement.';
    const bp = biddingProblem(li.objective, li.bidStrategy, bidMicros);
    if (bp) return bp;
    if (li.placements.some(isOffNetwork) && !li.advertiserDomain.trim()) return 'Publisher network placements need your website domain.';
    if (needsCategories(li.objective, li.productType, li.placements) && li.categories.length === 0) return 'Choose at least one IAB category that describes your brand.';
    if (needsApp(li.objective)) {
      const appId = li.appId.trim();
      if (!appId) return li.appStore === 'ios' ? 'Enter the App Store id of the app this line item promotes.' : 'Enter the Google Play package name of the app this line item promotes.';
      if (li.appStore === 'ios' && !/^\d{6,12}$/.test(appId)) return 'The App Store id is the number in the app\'s App Store link, e.g. 333903271.';
      if (li.appStore === 'android' && !/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)+$/.test(appId)) return 'The Google Play package name looks like com.company.app.';
    }
    const start = li.start ? Date.parse(li.start) : Date.now();
    if (li.end && Date.parse(li.end) <= start) return 'The end time must be after the start time.';
    return null;
  };

  // ── Actions ─────────────────────────────────────────────────────────────
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setStepError(null);
    try { await fn(); }
    catch (e) { setStepError(errMsg(e)); }
    finally { setBusy(false); }
  };

  const nextFromCampaign = () => {
    const p = campaignProblem();
    if (p) { setStepError(p); return; }
    if (campaign) { setStep(1); return; }
    run(async () => {
      const created = await xAdsService.createCampaign(uid, {
        name: campaignName.trim(), dailyBudgetMicros: toMicros(daily)!, totalBudgetMicros: toMicros(total) ?? undefined, fundingInstrumentId: fundingId || undefined, status: 'PAUSED',
      });
      setCampaign(created);
      if (!li.name) setLiField('name', `${campaignName.trim()} — line item`);
      toast.success('Campaign created on X (paused)');
      setStep(1);
    });
  };

  const nextFromLineItem = () => {
    const p = lineItemProblem();
    if (p) { setStepError(p); return; }
    setStepError(null);
    setStep(2);
  };

  const saveTargeting = () => {
    if (!campaign) { setStep(0); return; }
    run(async () => {
      if (!lineItem) {
        const created = await xAdsService.createLineItem(uid, {
          campaignId: campaign.id, name: li.name.trim(), objective: li.objective, productType: li.productType, placements: li.placements,
          bidStrategy: li.bidStrategy, bidAmountMicros: li.bidStrategy === 'AUTO' ? undefined : bidMicros ?? undefined, goal: li.goal || undefined,
          advertiserDomain: li.placements.some(isOffNetwork) ? li.advertiserDomain.trim() : undefined,
          categories: needsCategories(li.objective, li.productType, li.placements) ? li.categories.map((c) => c.value) : undefined,
          iosAppStoreIdentifier: needsApp(li.objective) && li.appStore === 'ios' ? li.appId.trim() : undefined,
          androidAppStoreIdentifier: needsApp(li.objective) && li.appStore === 'android' ? li.appId.trim() : undefined,
          startTime: localInputToIso(li.start), endTime: localInputToIso(li.end), status: 'PAUSED',
          targeting: targetingCount(targetingBody) ? targetingBody : undefined,
        });
        setLineItem(created);
        toast.success('Line item created on X (paused)');
        if (created.targetingError) {
          setTargetingError(created.targetingError);
          throw new Error(`The line item was created, but its targeting was not saved: ${created.targetingError}`);
        }
        setSavedTargeting(targetingSig);
        setTargetingError(null);
      } else if (savedTargeting !== targetingSig) {
        await xAdsService.setTargeting(uid, lineItem.id, targetingBody);
        setSavedTargeting(targetingSig);
        setTargetingError(null);
        toast.success('Targeting saved');
      }
      setStep(3);
    });
  };

  const promote = () => {
    if (!lineItem) { setStep(2); return; }
    if (videoCreative) {
      const added = new Set(mediaCreatives.map((m) => m.mediaKey));
      const keys = pickedVideos.filter((k) => !added.has(k));
      if (keys.length === 0) { setStepError(mediaCreatives.length ? null : 'Pick at least one video.'); if (mediaCreatives.length) setStep(4); return; }
      const lu = landingUrl.trim();
      if (lu && lu !== 'https://' && !/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(lu)) { setStepError('The landing page must be a full https:// address, or leave it empty.'); return; }
      run(async () => {
        const rows = await xAdsService.addMediaCreatives(uid, lineItem.id, keys, lu && lu !== 'https://' ? lu : undefined);
        setMediaCreatives((m) => [...m, ...rows]);
        toast.success(`${rows.length} video${rows.length === 1 ? '' : 's'} added to the line item`);
        setStep(4);
      });
      return;
    }
    const already = new Set(promoted.map((p) => p.tweetId));
    const ids = picked.filter((id) => !already.has(id));
    if (ids.length === 0) { setStepError(promoted.length ? null : 'Pick or create at least one post.'); if (promoted.length) setStep(4); return; }
    run(async () => {
      const rows = await xAdsService.promote(uid, lineItem.id, ids);
      setPromoted((p) => [...p, ...rows]);
      toast.success(`${rows.length} post${rows.length === 1 ? '' : 's'} added to the line item`);
      setStep(4);
    });
  };

  const [postId, setPostId] = useState('');
  const [findingPost, setFindingPost] = useState(false);
  const findPost = async () => {
    const pid = postId.trim();
    if (!/^\d{5,25}$/.test(pid)) { toast.error('A post ID is the number at the end of the post link.'); return; }
    setFindingPost(true);
    try {
      const [found] = await xAdsService.postsByIds(uid, [pid]);
      if (!found?.id) { toast.error('X has no post with that ID for this ads account.'); return; }
      onPostCreated(found);
      setPostId('');
    } catch { /* toasted */ } finally { setFindingPost(false); }
  };
  const onPostCreated = (post: XPost) => {
    if (!post.id) return;
    rememberPost(uid, post);
    setPosts((list) => [post, ...(list ?? []).filter((p) => p.id !== post.id)]);
    setPicked((p) => (p.includes(post.id!) ? p : [...p, post.id!]));
    setMode('pick');
  };

  const back = () => { setStepError(null); setStep((s) => Math.max(0, s - 1)); };
  const lineItemLocked = !!lineItem;
  const savedCount = savedTargeting ? targetingCount(JSON.parse(savedTargeting)) : 0;
  const objectives = constants?.objectives ?? ['ENGAGEMENTS'];
  const todayLocal = localDayStart();
  const pendingPicks = picked.filter((id) => !promoted.some((p) => p.tweetId === id)).length;
  const pendingVideos = pickedVideos.filter((k) => !mediaCreatives.some((m) => m.mediaKey === k)).length;

  return <XAdsShell section="New campaign" description="Campaign, line item, targeting and a promoted post — everything is created paused.">
    <ol className="xa-steps" aria-label="Wizard steps">
      {STEPS.map((s, i) => <li key={s} className={`xa-step ${i < step ? 'is-done' : ''}`} aria-current={i === step ? 'step' : undefined}>
        {i < step ? <Check size={13} /> : <span className="text-[11px]">{i + 1}</span>}{s}
      </li>)}
    </ol>
    {account?.sandbox && <XAdsNotice title="Sandbox account">Everything you create here stays in X's sandbox: it never serves and has no results.</XAdsNotice>}

    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
      <Card>
        {step === 0 && <section aria-label="Campaign">
          <h3 className="xa-panel-title mb-4">Campaign</h3>
          {campaign && <XAdsNotice title="Already created on X">This campaign exists (ID {campaign.id}). Change it later from the campaign list.</XAdsNotice>}
          <fieldset disabled={!!campaign} className="grid gap-x-6 md:grid-cols-2">
            <div className="md:col-span-2"><FormField label="Campaign name" htmlFor="xw-name" required><Input id="xw-name" maxLength={255} value={campaignName} onChange={(e) => setCampaignName(e.target.value)} /></FormField></div>
            <FormField label="Funding instrument" htmlFor="xw-fi" hint="How X charges this campaign">
              <XSelect id="xw-fi" value={funding ? fundingId : ''} onChange={setFundingId} disabled={!funding?.length}
                placeholder={!funding ? 'Loading…' : 'None on this ads account'}
                options={(funding ?? []).map((f) => ({ value: f.id, label: `${fundingName(f)}${f.currency ? ` · ${f.currency}` : ''}${f.ableToFund ? '' : ' · cannot fund'}` }))} />
            </FormField>
            <div />
            <FormField label={`Daily budget${currency ? ` (${currency})` : ''}`} htmlFor="xw-daily" required>
              <Input id="xw-daily" className="text-right tabular-nums" type="number" min="0" step="0.01" inputMode="decimal" value={daily} onChange={(e) => setDaily(e.target.value)} />
            </FormField>
            <FormField label={`Total budget${currency ? ` (${currency})` : ''}`} htmlFor="xw-total" hint="Optional">
              <Input id="xw-total" className="text-right tabular-nums" type="number" min="0" step="0.01" inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} />
            </FormField>
          </fieldset>
        </section>}

        {step === 1 && <section aria-label="Line item">
          <h3 className="xa-panel-title mb-4">Line item</h3>
          {lineItemLocked && <XAdsNotice title="Already created on X">This line item exists (ID {lineItem!.id}); its settings can no longer change here.</XAdsNotice>}
          <fieldset disabled={lineItemLocked} className="grid gap-x-6 md:grid-cols-2">
            <div className="md:col-span-2"><FormField label="Line item name" htmlFor="xw-li-name" required><Input id="xw-li-name" maxLength={255} value={li.name} onChange={(e) => setLiField('name', e.target.value)} /></FormField></div>
            <FormField label="Objective" htmlFor="xw-obj" required>
              <XSelect id="xw-obj" value={li.objective} onChange={(objective) => { if (objective) setLi((f) => fitToObjective(f, objective)); }}
                options={objectives.map((o) => ({ value: o, label: label(o) }))} />
            </FormField>
            <FormField label="Product type" htmlFor="xw-prod" hint={productsFor(li.objective).length === 1 ? 'The only one this objective takes' : undefined}>
              <XSelect id="xw-prod" value={li.productType} onChange={(v) => v && setLi((f) => {
                const placements = f.placements.filter((p) => placementsFor(f.objective, v).includes(p));
                return { ...f, productType: v, placements: placements.length ? placements : ['ALL_ON_TWITTER'] };
              })} options={productsFor(li.objective).map((p) => ({ value: p, label: productLabel(p) }))} />
            </FormField>
            <FormField label="Bid strategy" htmlFor="xw-bid" hint="Greyed = not for this objective">
              <XSelect id="xw-bid" value={li.bidStrategy} onChange={(v) => v && setLiField('bidStrategy', v)}
                options={(constants?.bidStrategies ?? ['AUTO']).map((b) => ({
                  value: b, label: b === 'AUTO' ? 'Automatic' : b === 'MAX' ? 'Maximum bid' : b === 'TARGET' ? 'Target bid' : label(b), disabled: !strategyAllowed(li.objective, b),
                }))} />
            </FormField>
            <FormField label={`Bid amount${currency ? ` (${currency})` : ''}`} htmlFor="xw-bid-amt" hint={li.bidStrategy === 'AUTO' ? 'X sets the bid' : 'Required'}>
              <Input id="xw-bid-amt" className="text-right tabular-nums" type="number" min="0" step="0.01" disabled={li.bidStrategy === 'AUTO'} value={li.bid} onChange={(e) => setLiField('bid', e.target.value)} />
            </FormField>
            <FormField label="Goal" htmlFor="xw-goal" hintBelow hint={li.objective === 'APP_INSTALLS'
              ? 'App installs / purchases need conversion events from your measurement partner; App clicks works without'
              : 'Optional — what X optimises for'}>
              <XSelect id="xw-goal" value={li.goal} onChange={(v) => setLiField('goal', v)} clearable
                options={[{ value: '', label: "X's default for the objective" }, ...goalsFor(li.objective).map((g) => ({ value: g, label: goalLabel(g) }))]} />
            </FormField>
            <div />
            <FormField label="Start" htmlFor="xw-start" hint="Your local time · empty = when enabled">
              <DateTimePicker value={li.start} minDate={todayLocal} onChange={(v) => setLiField('start', v)} placeholder="As soon as it is enabled" />
            </FormField>
            <FormField label="End" htmlFor="xw-end" hint="Optional">
              <DateTimePicker value={li.end} minDate={li.start ? new Date(li.start) : todayLocal} onChange={(v) => setLiField('end', v)} placeholder="No end date" />
            </FormField>
            <div className="md:col-span-2">
              <FormField label="Placements" hintBelow hint="Where the ads appear · All on X covers every X placement; single X placements don't mix with the publisher network">
                <div className="flex flex-wrap gap-2">
                  {placementsFor(li.objective, li.productType).map((p) => <label key={p} className={`xa-option !py-1.5 ${li.placements.includes(p) ? 'is-on' : ''}`}>
                    <XCheck className="mt-0.5" checked={li.placements.includes(p)} onChange={(on) => setLiField('placements', togglePlacement(li.placements, p, on))} />
                    {placementLabel(p)}
                  </label>)}
                </div>
              </FormField>
            </div>
            {li.placements.some(isOffNetwork) && <FormField label="Advertiser domain" htmlFor="xw-domain" required hint="Needed for publisher network placements">
              <Input id="xw-domain" placeholder="example.com" value={li.advertiserDomain} onChange={(e) => setLiField('advertiserDomain', e.target.value)} />
            </FormField>}
            {needsCategories(li.objective, li.productType, li.placements) && <FormField label="Brand categories (IAB)" htmlFor="xw-cat" required hint="What your business is — X asks for it here">
              <MultiPicker id="xw-cat" selected={li.categories} onChange={(v) => setLiField('categories', v.slice(0, 10))} options={categories ?? []}
                placeholder={categories ? 'Search a category' : 'Loading…'} />
            </FormField>}
            {needsApp(li.objective) && <>
              <FormField label="App store" htmlFor="xw-store" required hint="A line item promotes one app">
                <XSelect id="xw-store" value={li.appStore} onChange={(v) => v && setLi((f) => ({ ...f, appStore: v as 'ios' | 'android', appId: '' }))}
                  options={[{ value: 'ios', label: 'App Store (iPhone)' }, { value: 'android', label: 'Google Play (Android)' }]} />
              </FormField>
              <FormField label={li.appStore === 'ios' ? 'App Store id' : 'Google Play package name'} htmlFor="xw-app" required
                hint={li.appStore === 'ios' ? 'The number in the App Store link' : 'As in the Play Store link: id=…'}>
                <Input id="xw-app" inputMode={li.appStore === 'ios' ? 'numeric' : 'text'} placeholder={li.appStore === 'ios' ? '333903271' : 'com.company.app'}
                  value={li.appId} onChange={(e) => setLiField('appId', e.target.value)} />
              </FormField>
            </>}
          </fieldset>
        </section>}

        {step === 2 && <section aria-label="Targeting">
          <h3 className="xa-panel-title mb-4">Targeting</h3>
          {targetingError && <XAdsNotice title="Targeting was not saved" error>{targetingError} Adjust it and press "Save targeting" to try again, or continue without it.</XAdsNotice>}
          <TargetingForm uid={uid} constants={constants} value={targeting} onChange={setTargeting} />
        </section>}

        {step === 3 && videoCreative && <section aria-label="Creative">
          <h3 className="xa-panel-title mb-1">Creative — videos</h3>
          <p className="xa-muted mb-4">Pre-roll and Media line items run videos from the media library, not posts. Upload one under Creatives → Media library → Upload video.</p>
          {!videos && <div className="skeleton h-40 rounded-xl" aria-label="Loading videos" />}
          {videos?.length === 0 && <p className="xa-muted">No videos in the media library yet.</p>}
          {!!videos?.length && <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {videos.filter((v) => v.mediaKey).map((v) => {
              const done = mediaCreatives.some((m) => m.mediaKey === v.mediaKey);
              const on = done || pickedVideos.includes(v.mediaKey!);
              return <label key={v.mediaKey} className={`xa-option !flex-col !gap-2 !p-2 ${on ? 'is-on' : ''}`}>
                {v.posterUrl ? <img src={v.posterUrl} alt="" className="aspect-video w-full rounded-md object-cover" loading="lazy" /> : <div className="aspect-video w-full rounded-md bg-surface-alt" />}
                <span className="flex w-full items-center gap-2">
                  <XCheck checked={on} disabled={done} onChange={(c) => setPickedVideos((cur) => (c ? [...cur, v.mediaKey!] : cur.filter((k) => k !== v.mediaKey)))} />
                  <span className="min-w-0 truncate text-xs font-medium">{v.name || v.mediaKey}</span>
                  <span className="ml-auto shrink-0 text-[11px] text-text-muted">{v.durationMs ? `${Math.round(v.durationMs / 1000)}s` : ''}{done ? ' · added' : ''}</span>
                </span>
              </label>;
            })}
          </div>}
          <div className="mt-4 max-w-xl">
            <FormField label="Landing page" htmlFor="xw-landing" hint="Optional — where the video's link goes">
              <Input id="xw-landing" type="url" value={landingUrl} onChange={(e) => setLandingUrl(e.target.value)} placeholder="https://example.com" />
            </FormField>
          </div>
        </section>}

        {step === 3 && !videoCreative && <section aria-label="Creative">
          {creativeNeed(li.objective) && <XAdsNotice title="What this line item promotes">{creativeNeed(li.objective)}</XAdsNotice>}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="xa-panel-title">Creative</h3>
            <div className="xa-subtabs !mb-0" role="group" aria-label="Creative source">
              <button type="button" aria-pressed={mode === 'pick'} onClick={() => setMode('pick')}>Pick existing posts</button>
              <button type="button" aria-pressed={mode === 'create'} onClick={() => setMode('create')}>Create a post</button>
            </div>
          </div>
          {mode === 'create' && <PostComposer uid={uid} onCreated={onPostCreated} />}
          {mode === 'pick' && <>
            {!posts && <div className="skeleton h-40 rounded-xl" aria-label="Loading posts" />}
            {posts?.length === 0 && <p className="xa-muted mb-3">No promoted-only posts yet. <button type="button" className="underline" onClick={() => setMode('create')}>Create one</button>.</p>}
            <div className="mb-3 flex flex-wrap items-end gap-2">
              <div className="min-w-[14rem] flex-1 sm:max-w-sm">
                <FormField label="Add a post by its ID" htmlFor="xw-post-id" hint="X can take a while to list a new post">
                  <Input id="xw-post-id" inputMode="numeric" placeholder="2111360793216188839" value={postId} onChange={(e) => setPostId(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); findPost(); } }} />
                </FormField>
              </div>
              <Button variant="secondary" className="mb-4" loading={findingPost} disabled={!postId} onClick={findPost} iconLeft={<Plus size={14} />}>Add</Button>
            </div>
            {!!posts?.length && <div className="max-h-[55vh] space-y-2 overflow-y-auto">
              {posts.filter((p) => p.id).map((p) => {
                const on = picked.includes(p.id!);
                const done = promoted.some((x) => x.tweetId === p.id);
                return <label key={p.id} className={`xa-option ${on ? 'is-on' : ''}`}>
                  <XCheck className="mt-0.5" checked={on || done} disabled={done} onChange={(c) => setPicked((s) => (c ? [...s, p.id!] : s.filter((x) => x !== p.id)))} />
                  {p.mediaUrls?.[0] && <img src={p.mediaUrls[0]} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" loading="lazy" />}
                  <span className="min-w-0">
                    <span className="line-clamp-2 block">{postText(p.text) || p.name || `Post ${p.id}`}</span>
                    <span className="xa-muted block">{p.hasVideo ? 'Video · ' : ''}{p.cardUri ? 'Card · ' : ''}{dateTime(p.createdAt)} · {p.id}{done ? ' · added' : ''}</span>
                  </span>
                </label>;
              })}
            </div>}
          </>}
        </section>}

        {step === 4 && <section aria-label="Review">
          <h3 className="xa-panel-title mb-1">All set — everything is paused</h3>
          <p className="xa-muted mb-4">Nothing spends until you resume the campaign and the line item from the campaign list.</p>
          <dl className="grid gap-3 sm:grid-cols-2">
            {[
              ['Campaign', campaign ? `${campaign.name ?? campaignName} · ID ${campaign.id}` : '—'],
              ['Budget', `${microsMoney(toMicros(daily), currency)} daily${total ? ` · ${microsMoney(toMicros(total), currency)} total` : ''}`],
              ['Line item', lineItem ? `${lineItem.name ?? li.name} · ID ${lineItem.id}` : 'Not created'],
              ['Objective & bid', `${label(li.objective)} · ${li.bidStrategy === 'AUTO' ? 'Automatic bid' : `${label(li.bidStrategy)} ${microsMoney(bidMicros, currency)}`}`],
              ['Schedule', `${li.start ? dateTime(localInputToIso(li.start)) : 'When enabled'} → ${li.end ? dateTime(localInputToIso(li.end)) : 'no end'}`],
              ['Targeting', savedCount ? `${savedCount} criteria saved` : 'Everyone X allows'],
              videoCreative
                ? ['Videos', mediaCreatives.length ? mediaCreatives.map((m) => m.mediaKey).join(', ') : 'None — add one before enabling']
                : ['Promoted posts', promoted.length ? promoted.map((p) => p.tweetId).join(', ') : 'None — add one before enabling'],
            ].map(([k, v]) => <div key={k} className="rounded-lg bg-surface-alt/60 p-3"><dt className="text-[11px] text-text-muted">{k}</dt><dd className="mt-1 break-words text-sm font-medium">{v}</dd></div>)}
          </dl>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to={XA_HOME}><Button iconRight={<ArrowRight size={15} />}>Go to campaigns</Button></Link>
            <Button variant="secondary" onClick={() => { navigate(XA_NEW); window.location.reload(); }} iconLeft={<Plus size={15} />}>Create another</Button>
          </div>
        </section>}

        {stepError && <p role="alert" className="mt-4 flex items-start gap-1.5 text-sm text-danger"><XCircle size={15} className="mt-0.5 shrink-0" />{stepError}</p>}

        {step < 4 && <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={back} disabled={step === 0 || busy} iconLeft={<ArrowLeft size={15} />}>Back</Button>
          <div className="flex flex-wrap gap-2">
            {step === 2 && targetingError && lineItem && <Button variant="ghost" disabled={busy} onClick={() => { setStepError(null); setStep(3); }}>Continue without targeting</Button>}
            {step === 3 && <Button variant="ghost" disabled={busy} onClick={() => { setStepError(null); setStep(4); }}>Skip for now</Button>}
            {step === 0 && <Button loading={busy} onClick={nextFromCampaign} iconRight={<ArrowRight size={15} />}>{campaign ? 'Next' : stepError ? 'Try again' : 'Create campaign'}</Button>}
            {step === 1 && <Button onClick={nextFromLineItem} iconRight={<ArrowRight size={15} />}>Next</Button>}
            {step === 2 && <Button loading={busy} onClick={saveTargeting} iconLeft={stepError ? <RefreshCw size={14} /> : undefined} iconRight={<ArrowRight size={15} />}>
              {!lineItem ? 'Create line item' : savedTargeting !== targetingSig ? 'Save targeting' : 'Next'}
            </Button>}
            {step === 3 && videoCreative && <Button loading={busy} onClick={promote} iconRight={<ArrowRight size={15} />}>
              {pendingVideos ? `Add ${pendingVideos} video(s)` : 'Next'}
            </Button>}
            {step === 3 && !videoCreative && <Button loading={busy} onClick={promote} disabled={mode === 'create'} iconRight={<ArrowRight size={15} />}>
              {pendingPicks ? `Promote ${pendingPicks} post(s)` : 'Next'}
            </Button>}
          </div>
        </div>}
      </Card>

      <aside className="space-y-4">
        <Card>
          <p className="xa-eyebrow">CREATED ON X</p>
          <ul className="mt-3 space-y-3 text-sm">
            {[
              { k: 'Campaign', done: !!campaign, failed: false, v: campaign ? `ID ${campaign.id}` : 'Not yet' },
              { k: 'Line item', done: !!lineItem, failed: false, v: lineItem ? `ID ${lineItem.id}` : 'Not yet' },
              { k: 'Targeting', done: !!savedTargeting && !targetingError, failed: !!targetingError, v: targetingError ? 'Not saved — retry on step 3' : savedCount ? `${savedCount} criteria` : lineItem ? 'Everyone' : 'Not yet' },
              videoCreative
                ? { k: 'Videos', done: mediaCreatives.length > 0, failed: false, v: mediaCreatives.length ? `${mediaCreatives.length} video${mediaCreatives.length === 1 ? '' : 's'}` : 'Not yet' }
                : { k: 'Promoted posts', done: promoted.length > 0, failed: false, v: promoted.length ? `${promoted.length} post${promoted.length === 1 ? '' : 's'}` : 'Not yet' },
            ].map((r) => <li key={r.k} className="flex items-start gap-2">
              {r.failed ? <XCircle size={16} className="mt-0.5 shrink-0 text-danger" /> : r.done ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-success" /> : <CircleDashed size={16} className="mt-0.5 shrink-0 text-text-muted" />}
              <span><span className="block font-medium">{r.k}</span><span className="xa-muted">{r.v}</span></span>
            </li>)}
          </ul>
          {campaign && <a href="https://ads.x.com/" target="_blank" rel="noreferrer" className="xa-external mt-3 !px-0">Open in X Ads <ExternalLink size={13} /></a>}
        </Card>
        <Card>
          <p className="xa-eyebrow">GOOD TO KNOW</p>
          <p className="xa-muted mt-2">All line items of one campaign must share the same objective and product type. Maximum bid is not available for Reach or Followers; Target bid only for Reach, Followers and Website clicks.</p>
        </Card>
      </aside>
    </div>
  </XAdsShell>;
}
